import { useState, useRef, useEffect } from 'react';
import { IconChevronDown, IconCheck } from '@tabler/icons-react';
import {
    Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../ui/command';
import { cn } from '@/lib/utils';

/**
 * Searchable single-select combobox (shadcn Command + cmdk).
 * Renders the dropdown inline (no portal) so it works reliably inside a
 * Radix Sheet/Dialog focus trap.
 *
 * options: [{ value, label }]
 */
export default function SearchableSelect({
    value,
    onChange,
    options = [],
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found',
    disabled = false,
    className = '',
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selected = options.find(o => o.value === value);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className={cn(
                    'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm text-left focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed',
                    selected ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-400',
                    className
                )}
            >
                <span className="truncate">{selected ? selected.label : placeholder}</span>
                <IconChevronDown size={16} className="shrink-0 text-zinc-400" />
            </button>

            {open && (
                <div className="absolute z-[610] left-0 right-0 mt-1 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg">
                    <Command>
                        <CommandInput placeholder={searchPlaceholder} autoFocus />
                        <CommandList>
                            <CommandEmpty>{emptyText}</CommandEmpty>
                            <CommandGroup>
                                {options.map(opt => (
                                    <CommandItem
                                        key={opt.value}
                                        value={opt.label}
                                        onSelect={() => {
                                            onChange(opt.value);
                                            setOpen(false);
                                        }}
                                    >
                                        <IconCheck
                                            size={16}
                                            className={cn(
                                                'shrink-0',
                                                opt.value === value ? 'opacity-100 text-orange-500' : 'opacity-0'
                                            )}
                                        />
                                        <span className="truncate">{opt.label}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </div>
            )}
        </div>
    );
}
